import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { parse, tokenize, variableName } from '@the-real-insight/tri-model';

import { cn } from '@/lib/utils';
import { groupIcon } from './groupIcons';

/**
 * The formula field: an Excel-ish editor with syntax colouring and completion.
 *
 * A transparent textarea sits exactly on top of a coloured copy of the same text. The textarea does
 * the editing — caret, selection, undo, IME, screen readers — and the layer underneath only paints.
 * The alternative, a `contenteditable` that re-renders coloured spans as you type, has to restore
 * the caret after every keystroke, and that is the same trap as a number field that regroups while
 * you type. Here the browser never loses the caret because the element holding it never changes.
 *
 * The two layers must agree on metrics to the pixel, which is why both carry `LAYER` below: the
 * same font, size, padding and wrapping, in one place so they cannot drift apart.
 */

/** Font, padding and wrapping of both layers — see the note above; never set on only one of them. */
const LAYER =
    'm-0 box-border w-full min-h-16 whitespace-pre-wrap border-0 px-[11px] py-[9px] font-mono text-[13px] leading-[1.5] [overflow-wrap:break-word]';

/**
 * Syntax colours. A fixed scale rather than theme tokens: their whole job is to stay
 * distinguishable from one another, which is what the page has always used them for.
 */
const TOKEN_CLASS: Record<string, string> = {
    number: 'text-[#A8751A]',
    variable: 'font-semibold text-[#1F5C8B]',
    function: 'font-semibold italic text-[#6B470D]',
    parameter: 'text-[#1E8E5A]',
    operator: 'text-[#6B470D]',
    paren: 'text-muted-foreground',
    whitespace: 'text-inherit',
    unknown: 'text-[#C81E1E] underline decoration-[#C81E1E] decoration-wavy',
};

/** A name that is spelled like a variable but is not one of the catalogue's. */
const UNKNOWN_VARIABLE = 'font-semibold text-[#C81E1E] underline decoration-[#C81E1E] decoration-wavy';

export interface FormulaVariable {
    /** What goes into the formula. */
    name: string;
    /** What the reader sees in the list. */
    label: string;
    hint?: string;
    /** Where it sits in the sidebar: category, group (and kind) — shown and searched. */
    path?: string[];
    /** The sidebar block, for its icon. */
    block?: string;
    /** How the metric is converted to the score's granularity, where it does not sit on it. */
    note?: string | null;
}

/** How many matches the list shows; it scrolls, and a narrower word narrows it. */
const MAX_SUGGESTIONS = 60;

interface Properties {
    value: string;
    onChange: (value: string) => void;
    variables: FormulaVariable[];
    disabled?: boolean;
}

/** A variable needs brackets as soon as it carries anything but a plain name. */
const asToken = (name: string): string => (/^[A-Za-zÄÖÜäöüß_][A-Za-z0-9ÄÖÜäöüß_.]*$/.test(name) ? name : `[${name}]`);

/** The word being typed at the caret — what completion matches against. */
const wordAt = (source: string, caret: number): { text: string; start: number } => {
    let start = caret;
    while (start > 0 && /[A-Za-z0-9ÄÖÜäöüß_.[\] ]/.test(source[start - 1])) {
        start -= 1;
        // A bracket opens a name that may hold spaces; stop at its start.
        if (source[start] === '[') break;
    }
    // Do not run backwards over an operator into the previous term, nor keep the space before it.
    const text = source.slice(start, caret);
    const cut = Math.max(text.lastIndexOf('('), text.lastIndexOf(')'));
    const rest = cut >= 0 ? text.slice(cut + 1) : text;
    const from = cut >= 0 ? start + cut + 1 : start;
    const lead = rest.length - rest.trimStart().length;
    return { text: rest.slice(lead), start: from + lead };
};

export default function FormulaEditor({ value, onChange, variables, disabled }: Properties) {
    const { t } = useTranslation();
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const [caret, setCaret] = useState(0);
    const [open, setOpen] = useState(false);
    const [highlighted, setHighlighted] = useState(0);

    const parsed = useMemo(() => parse(value), [value]);
    // The server resolves names regardless of case, so the editor does too.
    const known = useMemo(() => new Set(variables.map((entry) => entry.name.toLowerCase())), [variables]);
    const haystacks = useMemo(
        () => variables.map((entry) => [entry.name, entry.label, ...(entry.path ?? [])].join(' ').toLowerCase()),
        [variables],
    );

    const tokens = useMemo(() => tokenize(value), [value]);

    const word = wordAt(value, caret);
    const suggestions = useMemo(() => {
        // Every word typed has to occur somewhere — in the name, the label or the path — so
        // "schule abstand" finds the distance to the nearest school.
        const words = word.text.replace(/^\[/, '').replace(/\]$/, '').trim().toLowerCase().split(/\s+/).filter(Boolean);
        if (!open || words.length === 0) return [];
        // Where a typed word starts a word of the name or label it is what was meant — „boden“ is
        // the Bodenrichtwert before the Spitzenbodenbeschleunigung; matches only inside a word or
        // only in the path come after.
        const starts = (entry: FormulaVariable, w: string) =>
            `${entry.label} ${entry.name}`.toLowerCase().split(/[^a-z0-9äöüß]+/).some((part) => part.startsWith(w));
        const matches: { entry: FormulaVariable; rank: number; index: number }[] = [];
        variables.forEach((entry, index) => {
            if (words.every((w) => haystacks[index].includes(w)))
                matches.push({ entry, index, rank: words.every((w) => starts(entry, w)) ? 0 : 1 });
        });
        return matches
            .sort((first, second) => first.rank - second.rank || first.index - second.index)
            .slice(0, MAX_SUGGESTIONS)
            .map((match) => match.entry);
    }, [open, word.text, variables, haystacks]);

    const insert = (variable: FormulaVariable) => {
        const before = value.slice(0, word.start);
        const after = value.slice(caret);
        const token = asToken(variable.name);
        const next = `${before}${token}${after}`;

        onChange(next);
        setOpen(false);

        // Put the caret after what was inserted, once React has written the new value.
        window.requestAnimationFrame(() => {
            const position = before.length + token.length;
            textareaRef.current?.setSelectionRange(position, position);
            textareaRef.current?.focus();
        });
    };

    const syncCaret = () => setCaret(textareaRef.current?.selectionStart ?? 0);

    return (
        <div className="flex flex-col gap-1">
            <div className="relative min-h-16 overflow-hidden rounded-md border bg-card">
                {/* Painted underneath; `aria-hidden` because the textarea already carries the text. */}
                <pre className={cn(LAYER, 'pointer-events-none')} aria-hidden>
                    {tokens.map((token, index) => {
                        const unknownVariable =
                            token.type === 'variable' && !known.has(variableName(token).toLowerCase());
                        return (
                            <span
                                key={`${token.start}-${index}`}
                                className={unknownVariable ? UNKNOWN_VARIABLE : TOKEN_CLASS[token.type]}
                            >
                                {token.text}
                            </span>
                        );
                    })}
                    {/* A trailing newline keeps the last line's height when the text ends in one. */}
                    {'\n'}
                </pre>

                <textarea
                    ref={textareaRef}
                    // The text itself is painted by the layer below; only the caret shows through.
                    className={cn(
                        LAYER,
                        'absolute inset-0 resize-y bg-transparent text-transparent caret-foreground outline-none selection:bg-foreground/20 focus:outline-none',
                    )}
                    value={value}
                    disabled={disabled}
                    spellCheck={false}
                    aria-label={t('heatmap.scores.formula')}
                    onChange={(event) => {
                        onChange(event.target.value);
                        setCaret(event.target.selectionStart ?? 0);
                        setOpen(true);
                        setHighlighted(0);
                    }}
                    onClick={() => {
                        syncCaret();
                        setHighlighted(0);
                    }}
                    onKeyUp={(event) => {
                        syncCaret();
                        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
                            setHighlighted(0);
                        }
                    }}
                    onBlur={() => window.setTimeout(() => setOpen(false), 120)}
                    onKeyDown={(event) => {
                        if (!open || suggestions.length === 0) {
                            if (event.key === 'Escape') setOpen(false);
                            return;
                        }

                        if (event.key === 'ArrowDown') {
                            event.preventDefault();
                            setHighlighted((current) => (current + 1) % suggestions.length);
                        } else if (event.key === 'ArrowUp') {
                            event.preventDefault();
                            setHighlighted((current) => (current - 1 + suggestions.length) % suggestions.length);
                        } else if (event.key === 'Enter' || event.key === 'Tab') {
                            event.preventDefault();
                            if (highlighted < suggestions.length) {
                                insert(suggestions[highlighted]);
                            }
                        } else if (event.key === 'Escape') {
                            setOpen(false);
                        }
                    }}
                />
            </div>

            {suggestions.length > 0 ? (
                <div
                    className="flex max-h-[280px] flex-col overflow-y-auto rounded-md border bg-card shadow-lg"
                    role="listbox"
                    data-testid="formula-suggestions"
                >
                    {suggestions.map((entry, index) => (
                        <button
                            key={entry.name}
                            type="button"
                            role="option"
                            aria-selected={index === highlighted}
                            ref={(element) => {
                                if (index === highlighted) element?.scrollIntoView({ block: 'nearest' });
                            }}
                            className={cn(
                                'flex items-start gap-2 px-2.5 py-1.5 text-left transition-colors hover:bg-accent',
                                index === highlighted && 'bg-accent',
                            )}
                            // mousedown, not click: blur would close the list first.
                            onMouseDown={(event) => {
                                event.preventDefault();
                                insert(entry);
                            }}
                            data-variable={entry.name}
                        >
                            {entry.block ? <span className="mt-0.5 shrink-0">{groupIcon(entry.block)}</span> : <></>}
                            <span className="flex min-w-0 flex-col gap-px">
                                <span className="text-xs">{entry.label}</span>
                                {entry.path?.length ? (
                                    <span className="truncate text-[10px] text-muted-foreground">{entry.path.join(' › ')}</span>
                                ) : (
                                    <></>
                                )}
                                {entry.note ? (
                                    <span className="text-[10px] text-[#A8751A]" data-testid="formula-suggestion-note">
                                        ↳ {entry.note}
                                    </span>
                                ) : (
                                    <></>
                                )}
                                {entry.hint ? (
                                    <span className="truncate font-mono text-[10px] text-muted-foreground/80">{entry.hint}</span>
                                ) : (
                                    <></>
                                )}
                            </span>
                        </button>
                    ))}
                </div>
            ) : (
                <></>
            )}

            <div
                className={cn(
                    'min-h-[15px] text-[11px]',
                    parsed.error ? 'text-destructive' : 'text-muted-foreground',
                )}
            >
                {parsed.error ?? (value.trim().length > 0 ? t('heatmap.scores.formulaValid') : ' ')}
            </div>
        </div>
    );
}
